import { themeAtom } from "../../shared/atoms";
import {
  CREW_ON_BOARD_COLUMN_LAYOUT,
  CREW_ON_BOARD_DATA_CELL_CLASS_NAME,
  CREW_ON_BOARD_DATA_SCROLLBAR_COLORS,
  CREW_ON_BOARD_HEADER_SCROLLBAR_COLORS,
  CREW_ON_BOARD_HEADER_CELL_CLASS_NAME,
  CREW_ON_BOARD_MISSING_VALUE,
  CREW_ON_BOARD_VERTICAL_SCROLLBAR_LAYOUT,
} from "../models/crew-on-board.constants";
import type { CrewOnBoardTableProps } from "../models/crew-on-board.models";
import { formatCrewOnBoardDate, formatCrewOnBoardMonths } from "../utils/crew-on-board.utils";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

const CrewOnBoardTable = ({ rows }: CrewOnBoardTableProps) => {
  const theme = useAtomValue(themeAtom);

  return (
    <section
      className={twMerge(
        "mx-auto mb-4 flex min-h-0 w-full max-w-[58rem] flex-1 flex-col overflow-hidden rounded-lg p-4 shadow-md",
        theme.home.tailwind.panelBackground,
        theme.home.tailwind.textDefault,
      )}
    >
      <h1 className="mb-1 shrink-0 pb-2 text-lg font-bold">Crew on board</h1>
      <div className="min-h-0 flex-1 overflow-x-auto">
        <table
          className={twMerge(
            "flex h-full w-full min-w-[51.5rem] flex-col overflow-hidden rounded-md border",
            theme.uwhcAssistant.border,
          )}
          aria-label="Crew on board report"
        >
          <thead
            className={twMerge(
              "block shrink-0 border-b",
              CREW_ON_BOARD_VERTICAL_SCROLLBAR_LAYOUT,
              CREW_ON_BOARD_HEADER_SCROLLBAR_COLORS,
              theme.uwhcAssistant.border,
            )}
          >
            <tr className={twMerge("grid", CREW_ON_BOARD_COLUMN_LAYOUT)}>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                #
              </th>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                Gender
              </th>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                Nationality
              </th>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                Designation / Rank
              </th>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                Contract duration
              </th>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                Sign on date
              </th>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                Change planned date
              </th>
              <th scope="col" className={CREW_ON_BOARD_HEADER_CELL_CLASS_NAME}>
                Months on board
              </th>
            </tr>
          </thead>
          <tbody
            className={twMerge(
              "block min-h-0 flex-1",
              CREW_ON_BOARD_VERTICAL_SCROLLBAR_LAYOUT,
              CREW_ON_BOARD_DATA_SCROLLBAR_COLORS,
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              theme.uwhcAssistant.tableScroll,
              theme.uwhcAssistant.scrolHover,
            )}
          >
            {rows.map((row) => (
              <tr
                key={row.crewRowNumber}
                className={twMerge(
                  "grid border-b last:border-b-0",
                  CREW_ON_BOARD_COLUMN_LAYOUT,
                  theme.uwhcAssistant.border,
                )}
              >
                <td className={CREW_ON_BOARD_DATA_CELL_CLASS_NAME}>{row.crewRowNumber}</td>
                <td className={CREW_ON_BOARD_DATA_CELL_CLASS_NAME}>{row.gender ?? CREW_ON_BOARD_MISSING_VALUE}</td>
                <td className={CREW_ON_BOARD_DATA_CELL_CLASS_NAME}>{row.nationality ?? CREW_ON_BOARD_MISSING_VALUE}</td>
                <td className={CREW_ON_BOARD_DATA_CELL_CLASS_NAME}>
                  {row.designationRank ?? CREW_ON_BOARD_MISSING_VALUE}
                </td>
                <td className={CREW_ON_BOARD_DATA_CELL_CLASS_NAME}>
                  {row.contractDurationMonths ?? CREW_ON_BOARD_MISSING_VALUE}
                </td>
                <td className={CREW_ON_BOARD_DATA_CELL_CLASS_NAME}>{formatCrewOnBoardDate(row.signOnDate)}</td>
                <td className={CREW_ON_BOARD_DATA_CELL_CLASS_NAME}>{formatCrewOnBoardDate(row.changePlannedDate)}</td>
                <td
                  className={twMerge(
                    CREW_ON_BOARD_DATA_CELL_CLASS_NAME,
                    row.isLongTenure && "font-bold text-red-600 dark:text-red-400",
                  )}
                >
                  {formatCrewOnBoardMonths(row.monthsOnBoard)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default CrewOnBoardTable;
